import React, { useState, useEffect } from "react";
import { List as ListEntity, Section as SectionEntity } from "@/entities/all";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, Plus, X, Eraser } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useData } from "../DataContext";
import type { List } from "@/types";

interface ListSettingsProps {
  list: List | null;
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
  onUpdate?: () => void;
}

export default function ListSettings({ list, open, onClose, onDelete }: ListSettingsProps) {
  const { folders, lists, sections, listItems, runOptimisticUpdate, refreshData } = useData();
  const [inheritedListIds, setInheritedListIds] = useState<string[]>(list?.inherited_list_ids || []);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCleaningEmptySections, setIsCleaningEmptySections] = useState(false);

  useEffect(() => {
    if (open) {
      setInheritedListIds(list?.inherited_list_ids || []);
    }
  }, [open, list]);

  const handleSave = async () => {
    if (!list) return;
    const originalState = { lists: [...lists] };
    const task = async () => ListEntity.update(list.id, { inherited_list_ids: inheritedListIds });
    const newState = {
      folders: [...folders],
      lists: lists.map(l => 
        l.id === list.id ? { ...l, inherited_list_ids: inheritedListIds } : l
      ),
      sections: [...sections],
      listItems: [...listItems]
    };

    await runOptimisticUpdate(task, { update: newState, originalState: { ...originalState, folders, sections, listItems } });
    onClose();
  };

  const handleDelete = async () => {
    if (!list) return;
    const originalState = { lists: [...lists] };
    const task = async () => ListEntity.delete(list.id);
    const newState = {
      folders: [...folders],
      lists: lists.filter(l => l.id !== list.id),
      sections: [...sections],
      listItems: [...listItems]
    };

    await runOptimisticUpdate(task, { update: newState, originalState: { ...originalState, folders, sections, listItems } });
    onDelete();
    onClose();
  };

  const addInheritedList = (listId: string) => {
    if (!inheritedListIds.includes(listId)) {
      setInheritedListIds([...inheritedListIds, listId]);
    }
  };

  const removeInheritedList = (listId: string) => {
    setInheritedListIds(inheritedListIds.filter(id => id !== listId));
  };

  const handleDeleteEmptySections = async () => {
    if (!list) return;
    setIsCleaningEmptySections(true);
    
    const emptySections = sections.filter(section => {
      if (section.list_id !== list.id) return false;
      const sectionItems = listItems.filter(item => item.section_id === section.id);
      return sectionItems.length === 0;
    });

    if (emptySections.length > 0) {
      await Promise.all(
        emptySections.map(section => 
          SectionEntity.delete(section.id).catch(err => {
            console.log('Section already deleted:', section.id, err);
          })
        )
      );
      await refreshData();
    }
    
    setIsCleaningEmptySections(false);
  };

  if (!list) return null;

  const availableLists = lists.filter(l => l.id !== list.id && !inheritedListIds.includes(l.id));
  const inheritedLists = lists.filter(l => inheritedListIds.includes(l.id));

  return (
    <>
      <Dialog open={open && !showDeleteConfirm} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-slate-900">
              List Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* List Info */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">List Information</h3>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{list?.title}</p>
                <p className="text-sm text-slate-600 mt-1">
                  Created {list?.created_date ? new Date(list.created_date).toLocaleDateString() : 'Recently'}
                </p>
              </div>
            </div>

            {/* Inherited Lists */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Inherited Lists</h3>
              <p className="text-sm text-slate-600 mb-4">
                Items from inherited lists will appear in this list with a special indicator.
              </p>
              
              {/* Current Inherited Lists */}
              {inheritedLists.length > 0 && (
                <div className="space-y-2 mb-4">
                  <AnimatePresence>
                    {inheritedLists.map((inheritedList) => (
                      <motion.div
                        key={inheritedList.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-blue-900">{inheritedList.title}</p>
                          <p className="text-sm text-blue-700">
                            Inheriting items from this list
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeInheritedList(inheritedList.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}

              {/* Available Lists to Inherit */}
              {availableLists.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Add lists to inherit from:
                  </Label>
                  <div className="max-h-60 overflow-y-auto space-y-2 border border-slate-200 rounded-lg p-3">
                    {availableLists.map((availableList) => (
                      <div
                        key={availableList.id}
                        className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg cursor-pointer"
                        onClick={() => addInheritedList(availableList.id)}
                      >
                        <div>
                          <p className="font-medium text-slate-900">{availableList.title}</p>
                          <p className="text-sm text-slate-600">
                            Click to inherit from this list
                          </p>
                        </div>
                        <Plus className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {availableLists.length === 0 && inheritedLists.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <p>No other lists available to inherit from.</p>
                  <p className="text-sm mt-1">Create more lists to enable inheritance.</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-6">
              <h3 className="font-semibold text-slate-900 mb-3">Actions</h3>
              <div className="p-4 border border-slate-200 bg-slate-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Delete empty sections</p>
                  <p className="text-sm text-slate-600">Remove all sections that have no items.</p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleDeleteEmptySections}
                  disabled={isCleaningEmptySections}
                  className="border-slate-200 hover:bg-slate-100"
                >
                  <Eraser className="w-4 h-4 mr-2" />
                  {isCleaningEmptySections ? 'Cleaning...' : 'Clean Up'}
                </Button>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="mt-6">
              <h3 className="font-semibold text-red-600 mb-2">Danger Zone</h3>
              <div className="p-4 border border-red-200 bg-red-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium text-red-900">Delete this list</p>
                  <p className="text-sm text-red-700">This action cannot be undone.</p>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-slate-200 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Save Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this list?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the list "{list.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
